import { useFeatureGate } from '@statsig/react-bindings';
import noop from 'lodash/noop';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { EventType } from '@/analytics/types';
import useContentScroll from '@/app/[locale]/store/_hooks/useContentScroll';
import { STORE_POP_PATHNAME } from '@/app/consts/pathnames';
import MembershipSelectionEnum from '@/app/enums/MembershipSelectionEnum';
import WarrantySelectionEnum from '@/app/enums/WarrantySelectionEnum';
import logToDatadog from '@/logger/logToDatadog';
import { addToCart } from '@/queries/AddToCart';
import { changeWarranty } from '@/queries/ChangeWarranty';
import { type ListProduct, useAddOnBundleData } from '@/queries/Products';
import { getCartIdCookie } from '@/utils/cartUtils';
import { getCookie } from '@/utils/cookie';

import sendBundleAnalytics from '../_analytics/sendBundleAnalytics';
import type { MembershipObject } from '../_types/MembershipObject';
import type { WarrantyObject } from '../_types/WarrantyObject';
import buildWarrantyObject from '../_utils/buildWarrantyObject';
import getExtendedWarrantySku from '../_utils/getExtendedWarrantySku';
import getMembershipSku from '../_utils/getMembershipSku';

interface WarrantyInput {
  warrantyInput: WarrantySelectionEnum;
}
interface MembershipInput {
  membershipInput: MembershipSelectionEnum;
}
interface ModalOpenInput {
  modalOpenInput: boolean;
}

export interface AddOnBundleProviderValues {
  readonly isLoadingProductData: boolean;
  readonly oneYearMembershipData: ListProduct | undefined;
  readonly twoYearWarrantyData: ListProduct | undefined;
  readonly threeYearWarrantyData: ListProduct | undefined;
  readonly reachedThreeRingLimit: boolean;
  readonly membershipSelection: MembershipSelectionEnum;
  readonly proceedButtonText: string;
  readonly isModalOpen: boolean;
  readonly isAddingToCart: boolean;
  readonly warrantySelection: WarrantySelectionEnum;
  readonly onMembershipSelection: ({
    membershipInput,
  }: MembershipInput) => void;
  readonly onWarrantySelection: ({ warrantyInput }: WarrantyInput) => void;
  readonly onOpenModal: ({ modalOpenInput }: ModalOpenInput) => void;
  readonly onAddToCart: () => void;
  scrollRef: (node: HTMLDivElement) => void;
}

export const DEFAULT_VALUES: AddOnBundleProviderValues = {
  isLoadingProductData: true,
  oneYearMembershipData: undefined,
  twoYearWarrantyData: undefined,
  threeYearWarrantyData: undefined,
  reachedThreeRingLimit: false,
  membershipSelection: MembershipSelectionEnum.OneMonth,
  warrantySelection: WarrantySelectionEnum.OneYear,
  proceedButtonText: 'bundle_modal_skip_button',
  isModalOpen: false,
  isAddingToCart: false,
  onMembershipSelection: noop,
  onWarrantySelection: noop,
  onOpenModal: noop,
  onAddToCart: noop,
  scrollRef: noop,
};

export const AddOnBundleContext =
  createContext<AddOnBundleProviderValues>(DEFAULT_VALUES);

export const useAddOnBundleContext = (): AddOnBundleProviderValues => {
  return useContext(AddOnBundleContext);
};

const AddOnBundleProvider = ({ children }: PropsWithChildren) => {
  const productData = useAddOnBundleData();
  const router = useRouter();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const productsQueryParams = searchParams.get('products') ?? '';
  const isUS = useFeatureGate('usa_only').value;

  const [reachedThreeRingLimit, setReachedThreeRingLimit] =
    useState<boolean>(false);

  const [membershipSelection, setMembershipSelection] =
    useState<MembershipSelectionEnum>(MembershipSelectionEnum.OneMonth);

  const [warrantySelection, setWarrantySelection] =
    useState<WarrantySelectionEnum>(WarrantySelectionEnum.OneYear);

  const [proceedButtonText, setProceedButtonText] = useState(
    'bundle_modal_skip_button',
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  useEffect(() => {
    if (!isUS || locale !== 'en') {
      router.push(`/${locale}${STORE_POP_PATHNAME}`);
    }
  }, [isUS, locale, router]);

  useEffect(() => {
    const requestedRingCount = productsQueryParams
      .split(',')
      .reduce((acc, cur) => {
        const splitArr = cur.split(':');
        const numericQuantity = parseInt(splitArr[1] ?? '');
        if (!isNaN(numericQuantity)) {
          return acc + numericQuantity;
        }
        return acc;
      }, 0);
    if (requestedRingCount >= 3) {
      setReachedThreeRingLimit(true);
    } else if (requestedRingCount == 0) {
      router.push(`/${locale}${STORE_POP_PATHNAME}/oura-ring-4/silver`);
      setReachedThreeRingLimit(false);
    } else {
      setReachedThreeRingLimit(false);
    }
  }, [productsQueryParams, locale, router]);

  const oneYearMembership = useMemo(() => {
    return productData.data?.oneYearMembership;
  }, [productData]);

  const twoYearWarranty = useMemo(() => {
    return productData.data?.twoYearWarranty;
  }, [productData]);

  const threeYearWarranty = useMemo(() => {
    return productData.data?.threeYearWarranty;
  }, [productData]);

  const handleMembershipSelection = useCallback(
    ({ membershipInput }: MembershipInput) => {
      if (membershipInput == membershipSelection) {
        setMembershipSelection(MembershipSelectionEnum.OneMonth);
      } else {
        setMembershipSelection(membershipInput);
      }
    },
    [membershipSelection],
  );

  const handleWarrantySelection = useCallback(
    ({ warrantyInput }: WarrantyInput) => {
      if (warrantyInput == warrantySelection) {
        setWarrantySelection(WarrantySelectionEnum.OneYear);
      } else {
        setWarrantySelection(warrantyInput);
      }
    },
    [warrantySelection],
  );

  const handleOpenModal = useCallback(({ modalOpenInput }: ModalOpenInput) => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'add to cart',
      action: 'open bundle modal',
      location: 'body',
      path: '/meta/what-to-expect',
      step: 1,
    });
    setIsModalOpen(modalOpenInput);
  }, []);

  useEffect(() => {
    if (
      membershipSelection === MembershipSelectionEnum.OneMonth &&
      warrantySelection === WarrantySelectionEnum.OneYear
    ) {
      setProceedButtonText('bundle_modal_skip_button');
    } else {
      setProceedButtonText('pdp_add_to_cart');
    }
  }, [membershipSelection, warrantySelection]);

  const contentScroller = useContentScroll();

  //TODO: we will need to handle adding a gen 3 sizing kit to cart if we end up adding those and change this if we end up just shipping by size
  const handleAddAddOnsToCart = useCallback(async (): Promise<void> => {
    try {
      const warrantySku = getExtendedWarrantySku(warrantySelection) ?? '';

      const membershipSku = getMembershipSku(membershipSelection);
      void window.ouraAnalytics.track(EventType.CTAClicked, {
        cta:
          proceedButtonText === 'pdp_add_to_cart'
            ? 'add to cart'
            : 'skip this step',
        action: 'open bundle modal',
        location: 'add_on_module',
        path: '/cart',
      });
      setIsAddingToCart(true);
      if (membershipSelection === MembershipSelectionEnum.OneYear) {
        await addToCart(membershipSku, warrantySku);
      }
      if (warrantySelection !== WarrantySelectionEnum.OneYear) {
        await changeWarranty(warrantySku);
      }
      const fbc = getCookie('_fbc');
      const fbp = getCookie('_fbp');
      const cartId = getCartIdCookie();
      const url = `https://www.ouraring.com/meta/what-to-expect`;

      const membershipObj: MembershipObject = {
        currency: oneYearMembership?.price?.currencyCode ?? 'USD',
        fbc,
        fbp,
        image_url: oneYearMembership?.images?.[0]?.originalSrc,
        membershipSelection,
        price: oneYearMembership?.price?.amount,
        product_id: oneYearMembership?.id,
        sku: membershipSku,
        url,
        variant: oneYearMembership?.variants[0]?.productId,
      };
      const warrantyObj: WarrantyObject = buildWarrantyObject({
        fbc,
        fbp,
        image_url: twoYearWarranty?.images?.[0]?.originalSrc,
        url,
        twoYearWarranty,
        threeYearWarranty,
        warrantySelection,
      });

      setIsAddingToCart(false);
      void sendBundleAnalytics({
        cart_id: cartId,
        membershipObj,
        warrantyObj,
      });
      router.push(`/${locale}/cart`);
    } catch (error) {
      logToDatadog({
        error,
        message: 'Error thrown during addToCart (cart 2.0)',
        module: 'cart',
      });
    }
  }, [
    locale,
    membershipSelection,
    oneYearMembership?.id,
    oneYearMembership?.images,
    oneYearMembership?.price?.amount,
    oneYearMembership?.price?.currencyCode,
    oneYearMembership?.variants,
    proceedButtonText,
    router,
    threeYearWarranty,
    twoYearWarranty,
    warrantySelection,
  ]);

  const value: AddOnBundleProviderValues = useMemo(
    () => ({
      ...contentScroller,
      isLoadingProductData: productData.isLoading,
      oneYearMembershipData: oneYearMembership,
      twoYearWarrantyData: twoYearWarranty,
      threeYearWarrantyData: threeYearWarranty,
      reachedThreeRingLimit: reachedThreeRingLimit,
      membershipSelection,
      warrantySelection,
      proceedButtonText,
      isModalOpen,
      isAddingToCart,
      onMembershipSelection: handleMembershipSelection,
      onWarrantySelection: handleWarrantySelection,
      onOpenModal: handleOpenModal,
      onAddToCart: handleAddAddOnsToCart,
    }),
    [
      contentScroller,
      handleMembershipSelection,
      handleWarrantySelection,
      handleOpenModal,
      handleAddAddOnsToCart,
      isAddingToCart,
      productData.isLoading,
      oneYearMembership,
      twoYearWarranty,
      threeYearWarranty,
      reachedThreeRingLimit,
      membershipSelection,
      warrantySelection,
      proceedButtonText,
      isModalOpen,
    ],
  );

  return (
    <AddOnBundleContext.Provider value={value}>
      {children}
    </AddOnBundleContext.Provider>
  );
};

export default AddOnBundleProvider;
